import React, { useEffect } from 'react';
import {
  FlexBox,
  Typography,
  Button,
  Paginator,
  Grid,
  useMediaQuery,
  useTheme,
  MuiBox
} from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useActions } from 'hooks/useActions';
import { actions as eventActions, EventPayload, eventNames } from 'services/events';
import { selectors as hardwareSelectors, actions as hardwareActions } from 'services/hardware';
import { selectors as kioskSelectors, actions as kioskActions, Kiosk } from 'services/kiosk';
import { ActionOverlay } from 'components/ActionOverlay';
import { EventData } from './components/EventData';
import { BarcodeScanner } from './components/BarcodeScanner';
import { PaymentDevice } from './components/PaymentDevice';
import TicketPrinter from './components/TicketPrinter';
import { OPERATIONAL, UNKNOWN, NO_PING } from 'constants/hardware';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { SystemsPagination } from 'components/Pagination/components/SystemsPagination';
import { useStyles } from './styled';

const { useState } = React;

const SystemsCheckView = () => {
  // Action Creators and Selectors
  const { logEvent } = useActions(eventActions);
  const { eventDataPing, pingPrinters } = useActions(hardwareActions);
  const { rebootKiosk, updateKiosk } = useActions(kioskActions);

  const eventData = useSelector(hardwareSelectors.selectEventData);
  const statuses = useSelector(hardwareSelectors.selectAllStatuses);
  const kioskPrinters = useSelector(kioskSelectors.selectPrinters);
  const paymentHardware = useSelector(kioskSelectors.selectPaymentHardware);
  const scannerHardware = useSelector(kioskSelectors.selectScannerHardware);
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const kioskLoading = useSelector(kioskSelectors.selectLoading);

  // Sets the current page of the list of devices
  const [currentPage, setCurrentPage] = React.useState(1);
  const [showRebootOverlay, setShowRebootOverlay] = useState(false);
  const [rebooting, setRebooting] = useState(false);

  const classes = useStyles();
  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.down('lg'));
  const cardWidthMax = { maxWidth: '390px' };

  const hardwareDevices: { type: string; id?: string }[] = [];
  if (scannerHardware) hardwareDevices.push({ type: 'SCANNER' });
  if (paymentHardware) hardwareDevices.push({ type: 'PAYMENT_DEVICE' });
  if (kioskPrinters.length > 0) {
    kioskPrinters.forEach((p) => hardwareDevices.push({ type: 'PRINTER', id: p.id }));
  }
  hardwareDevices.push({ type: 'EVENT_DATA' });

  // Determines if setup is complete
  const setupComplete = statuses.every((status) => [OPERATIONAL, NO_PING].includes(status));
  // Determines if there is any unknown status
  const hasUnknownStatus = statuses.some((status) => status === UNKNOWN);

  useEffect(() => {
    if (eventData.status.code === UNKNOWN) eventDataPing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Ping the printers after the kiosk has been fetched, and the printers are registered in the hardware store
    if (!kioskLoading && hasUnknownStatus) {
      if (kioskPrinters.length > 0) {
        pingPrinters({});
      }
    }
  }, [kioskLoading]);

  const onSetupComplete = () => {
    const eventPayload: EventPayload = {
      event: eventNames.HARDWARE_CONNECTTION_CONFIRMED,
      category: 'hardware_status'
    };
    logEvent(eventPayload);

    updateKiosk({ setupComplete: true });
  };

  const getHardwareDevice = (device: { type: string; id?: string }) => {
    let elem = null;
    switch (device.type) {
      case 'EVENT_DATA':
        elem = <EventData />;
        break;
      case 'SCANNER':
        elem = <BarcodeScanner />;
        break;
      case 'PRINTER':
        elem = <TicketPrinter kioskHardwareId={device.id as string} />;
        break;
      case 'PAYMENT_DEVICE':
        elem = <PaymentDevice />;
        break;
      default:
        break;
    }

    return elem;
  };

  const onRebootConfirm = () => {
    setRebooting(true);
    rebootKiosk();
  };

  if (kiosk.setupComplete) return <Navigate to="/startup" />;

  return (
    <>
      {showRebootOverlay && (
        <ActionOverlay
          title={
            rebooting ? (
              <Typography variant="h2" align="center" sx={{ lineHeight: 1 }}>
                This kiosk will <br /> reboot momentarily.
              </Typography>
            ) : (
              'Are you sure you want to reboot this kiosk?'
            )
          }
          cancelText={rebooting ? undefined : 'Reboot'}
          onCancel={onRebootConfirm}
          confirmText={rebooting ? undefined : 'Back'}
          onConfirm={() => setShowRebootOverlay(false)}
        />
      )}

      <MuiBox className={classes.systemCheckOuterBox}>
        {hasUnknownStatus && <LoadingOverlay message="Please wait while we confirm the hardware status of the kiosk" />}
        <Grid container spacing={2} className={classes.systemCheckInnerBox}>
          {/* Header */}
          <Grid item md={12} xl={12} className={classes.systemCheckHeaderContainer}>
            <MuiBox className={classes.systemCheckHeaderBox}>
              <Typography className={classes.systemCheckHeader}>{kiosk.name} Systems Check</Typography>
              <Typography className={classes.systemCheckSubHeader}>
                Please ensure all of your hardware is setup and operational
              </Typography>
            </MuiBox>
          </Grid>
          <Grid container className={classes.systemCheckDeviceInnerContainer}>
            <Paginator
              paginationComponent={SystemsPagination}
              items={hardwareDevices}
              itemsPerPage={matchPortrait ? 6 : 4}
              page={currentPage}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            >
              {({ items }) => (
                <>
                  {matchPortrait ? (
                    <Grid container spacing={2} className={classes.systemCheckPortraitContainer}>
                      {items.map((device, idx) => (
                        <Grid item md={6} xl={3} sx={cardWidthMax} key={`hardware-${idx}`}>
                          {getHardwareDevice(device)}
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={3} className={classes.systemCheckLandscapeContainer}>
                      {items.map((device, idx) => (
                        <Grid item md={6} xl={3} sx={cardWidthMax} lg={3} key={`hardware-${idx}`}>
                          {getHardwareDevice(device)}
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </Paginator>
          </Grid>
          <Grid container className={classes.systemCheckButtonsContainer} spacing={4}>
            <Grid item md={12} xl={6} lg={6}>
              <FlexBox sx={{ justifyContent: 'center' }}>
                {/* Reboot Kiosk Button */}
                <Button width={475} customSize="giant" appearance="white" onClick={() => setShowRebootOverlay(true)}>
                  Reboot Kiosk
                </Button>
              </FlexBox>
            </Grid>
            <Grid item md={12} xl={6} lg={6}>
              {/* Complete Setup Button */}
              <FlexBox sx={{ justifyContent: 'center' }}>
                <Button
                  customSize="giant"
                  endIconName="next_circle"
                  disabled={!setupComplete}
                  onClick={onSetupComplete}
                  appearance="primary"
                  data-testid="complete-setup-button"
                >
                  Complete Setup
                </Button>
              </FlexBox>
            </Grid>
          </Grid>
        </Grid>
      </MuiBox>
    </>
  );
};

export default SystemsCheckView;
